import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import config from '../../../../config';

const API = config.API_ADDRESS;

async function authHeaders() {
  try {
    const { tokens } = await fetchAuthSession();
    const user = await getCurrentUser();
    if (tokens?.idToken && user?.username) {
      return {
        Authorization: `Bearer ${tokens.idToken.toString()}`,
        'user-id': user.username,
      };
    }
  } catch {
    /* anonymous respondent */
  }
  return {};
}

async function apiFetch(url, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(await authHeaders()),
    ...(options.headers || {}),
  };
  const res = await fetch(url, { ...options, headers });
  let body = null;
  try { body = await res.json(); } catch { /* may be 204 */ }
  if (!res.ok) {
    const err = new Error((body && body.error) || `Request failed: ${res.status}`);
    err.response = { status: res.status, data: body || {} };
    throw err;
  }
  return body;
}

function withTest(url, test) {
  return test ? `${url}${url.includes('?') ? '&' : '?'}test=true` : url;
}

/**
 * Thin wrapper over the Phase 3 {@code /form-flow/*} endpoints plus the existing
 * {@code /component-service/dynamic-form/*} magic-link endpoints (reused for any flow step
 * gated by {@code EMAIL_MAGIC_LINK} — the server's {@code expectedShareId} check matches the
 * magic-link grant against the flow's current step componentId, so no new endpoint is needed).
 *
 * All methods propagate the {@code test} flag automatically and thread the optional
 * {@code grantToken} through as the {@code x-verification-grant-token} header.
 */
export function createFormFlowApi(formSessionId, { test = false, grantToken = null } = {}) {
  function headers() {
    const h = {};
    if (grantToken) h['x-verification-grant-token'] = grantToken;
    return h;
  }

  return {
    async getSession() {
      return apiFetch(
        withTest(`${API}/form-flow/session/${formSessionId}`, test),
        { method: 'GET', headers: headers() },
      );
    },
    async submit(componentId, answers) {
      return apiFetch(
        withTest(`${API}/form-flow/session/${formSessionId}/submit`, test),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers() },
          body: JSON.stringify({ componentId, answers }),
        },
      );
    },
    async saveDraft(componentId, answers) {
      return apiFetch(
        withTest(`${API}/form-flow/session/${formSessionId}/draft`, test),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers() },
          body: JSON.stringify({ componentId, answers }),
        },
      );
    },
    async back(targetComponentId) {
      return apiFetch(
        withTest(`${API}/form-flow/session/${formSessionId}/back`, test),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers() },
          body: JSON.stringify({ targetComponentId }),
        },
      );
    },
    streamUrl() {
      return withTest(`${API}/form-flow/session/${formSessionId}/stream`, test);
    },
  };
}

/**
 * Request a magic-link email for the current flow step. Re-uses the per-component endpoint
 * that standalone dynamic forms already use — the magic link that arrives will contain a
 * {@code verifyToken} that, once exchanged via {@link verifyMagicLink}, yields a grant token
 * scoped to this component.
 */
export async function requestFlowMagicLink(componentId, email) {
  return apiFetch(
    `${API}/component-service/dynamic-form/${componentId}/magic-link/request`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    },
  );
}

/**
 * Exchange the {@code verifyToken} from the inbound magic link for a grant token + verified
 * email. Global endpoint — no componentId scoping needed at this step; the resulting grant
 * token is already bound to a specific componentId by the server.
 */
export async function verifyFlowMagicLink(verificationToken) {
  return apiFetch(
    `${API}/component-service/dynamic-form/magic-link/verify`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verificationToken }),
    },
  );
}
